/**
 * Trace registry contracts (Session 4a of the P1 head-to-head roadmap).
 *
 * The runner already drops Playwright trace.zip files under the
 * configured `runner.traceDir` and pins the absolute path on
 * `test_runs.trace_path` (Migration 027). The `traces` table
 * (Migration 081) layers a stable id + status + producer + retention
 * pointer on top so the upcoming Trace Viewer can address traces by id
 * instead of guessing from a run's row.
 *
 * Single source of truth for the wire shape; the API validates inbound
 * writes against these schemas and the frontend infers TS types.
 */

import { z } from 'zod';

export const TraceStatusSchema = z.enum(['pending', 'ready', 'failed', 'expired']);
export type TraceStatus = z.infer<typeof TraceStatusSchema>;

export const TraceStorageBackendSchema = z.enum(['local', 'b2', 's3']);
export type TraceStorageBackend = z.infer<typeof TraceStorageBackendSchema>;

export const TraceEnabledBySchema = z.enum(['flow', 'admin', 'both']);
export type TraceEnabledBy = z.infer<typeof TraceEnabledBySchema>;

export const TraceMetadataSchema = z.object({
  id: z.string().uuid(),
  runId: z.string().uuid(),
  orgId: z.string().uuid(),
  storageKey: z.string().min(1),
  storageBackend: TraceStorageBackendSchema,
  sizeBytes: z.number().int().nonnegative(),
  status: TraceStatusSchema,
  enabledBy: TraceEnabledBySchema,
  createdAt: z.string(),
  expiresAt: z.string().nullable(),
});
export type TraceMetadata = z.infer<typeof TraceMetadataSchema>;

export const GetTraceMetadataResponseSchema = z.object({
  trace: TraceMetadataSchema,
});
export type GetTraceMetadataResponse = z.infer<typeof GetTraceMetadataResponseSchema>;

export const ListTracesForRunResponseSchema = z.object({
  runId: z.string().uuid(),
  traces: z.array(TraceMetadataSchema),
});
export type ListTracesForRunResponse = z.infer<typeof ListTracesForRunResponseSchema>;

/**
 * Default retention window for trace bytes. The retention worker
 * (already in place for run rows) gets a parallel sweep over this
 * window in Session 4c. Operators can override per-plan via the
 * existing `runRetention` admin config.
 */
export const TRACE_DEFAULT_RETENTION_DAYS = 30;

/** v0.46.1 — headless automation trace capture (default off). */
export const AutomationTraceModeSchema = z.enum(['off', 'on']);
export type AutomationTraceMode = z.infer<typeof AutomationTraceModeSchema>;
